import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  AssignmentActionDef,
  AssignmentFilterData,
} from "./AssignmentFilterPopover";
import { AssignmentData, HeaderAssignmentHeader } from "./models";
import ReactTooltip from "react-tooltip";
import { CheckAssignmentTableHeaderCell } from "./CheckAssignmentTableHeaderCellProps";

/** Left rule on every Nth task column (1-based) for horizontal scan alignment. */
export const CHECK_ASSIGNMENT_TASK_COLUMN_GROUP = 10;

const CHECK_ASSIGNMENT_COLUMN_GROUP_BORDER_ROTATION = [
  "border-l-2 border-l-blue-400 min-w-0",
  "border-l-2 border-l-amber-400 min-w-0",
  "border-l-2 border-l-emerald-400 min-w-0",
  "border-l-2 border-l-violet-400 min-w-0",
] as const;

/** Border classes for every Nth task column; color cycles every band (4 colors). */
export function checkAssignmentTaskColumnGroupClasses(taskIndex: number): string {
  if ((taskIndex + 1) % CHECK_ASSIGNMENT_TASK_COLUMN_GROUP !== 0) {
    return "";
  }
  const band =
    Math.floor(taskIndex / CHECK_ASSIGNMENT_TASK_COLUMN_GROUP) %
    CHECK_ASSIGNMENT_COLUMN_GROUP_BORDER_ROTATION.length;
  return CHECK_ASSIGNMENT_COLUMN_GROUP_BORDER_ROTATION[band];
}

export function CheckAssignmentTableHeader(props: {
  headers: HeaderAssignmentHeader[];
  data: AssignmentData[];
  onAction: AssignmentActionDef;
  filterKeys?: { [key: string]: AssignmentFilterData[] };
  shownData?: AssignmentData[];
  domain: "workflow" | "blueprint";
}) {
  const userNameFilterData: AssignmentFilterData[] = props.data.map((row) => ({
    key: row.actor_uuid,
    label: row.email,
  }));
  const lastActiveFilterData: AssignmentFilterData[] = props.data.map(
    (row) => ({
      key: row.actor_uuid,
      label: row?.lastActive ?? "",
    })
  );
  const lastTaskFilterData: AssignmentFilterData[] = props.data.map((row) => ({
    key: row.actor_uuid,
    label: row?.lastTask ?? "",
  }));

  function getFilterData(taskKey: string) {
    const ldata = props.data.map((row) => ({
      key: row.actor_uuid,
      label: row?.tasks.find((t) => t.key === taskKey)
        ? "assigned"
        : "unassigned",
    }));
    return ldata;
  }

  return (
    <div className="flex bg-gray-100 rounded-t-xl">
      <CheckAssignmentTableHeaderCell
        hasIcon={true}
        filterData={userNameFilterData}
        onAction={(action, selection) =>
          props.onAction(action, selection, "email")
        }
        filterCount={props.shownData?.length}
        headerKey="email"
        filterApplied={!!props.filterKeys?.["email"]?.length}
      >
        User Name
      </CheckAssignmentTableHeaderCell>
      {props.domain === "workflow" && (
        <>
          <div className="flex-1 items-center justify-center p-0">
            <div className="flex items-center justify-center font-bold w-[30rem]">
              <div className="flex items-center justify-center grow text-sm">
                <span
                  data-tip={
                    "No time value is shown for users who have been inactive for longer than 6 hours"
                  }
                  className="relative flex items-center"
                >
                  <span className="mr-2">{"ACTIVITY"}</span>
                  <InformationCircleIcon height={20} width={20} color="grey" />
                  <ReactTooltip
                    offset={{ right: 35 }}
                    className="flex hover:visible:opacity-1 w-[30rem]"
                    delayHide={1000}
                    effect="solid"
                  />
                </span>
              </div>
            </div>

            <div className="flex">
              <CheckAssignmentTableHeaderCell
                hasIcon={true}
                filterData={lastActiveFilterData}
                onAction={(action, selection) =>
                  props.onAction(action, selection, "lastActive")
                }
                filterCount={props.shownData?.length}
                headerKey="lastActive"
                isSubCell={true}
                filterApplied={!!props.filterKeys?.["lastActive"]?.length}
              >
                Last Active
              </CheckAssignmentTableHeaderCell>
              <CheckAssignmentTableHeaderCell
                onAction={(action, selection) =>
                  props.onAction(action, selection, "lastTask")
                }
                filterData={lastTaskFilterData}
                filterCount={props.shownData?.length}
                headerKey="lastTask"
                isSubCell={true}
                hasIcon={true}
                filterApplied={!!props.filterKeys?.["lastTask"]?.length}
              >
                Task
              </CheckAssignmentTableHeaderCell>
            </div>
          </div>
        </>
      )}
      {props.headers.map((task, taskIndex) => {
        return (
          <CheckAssignmentTableHeaderCell
            key={task.key}
            hasIcon={true}
            onAction={(action, selection) =>
              props.onAction(action, selection, task.key)
            }
            filterCount={props.shownData?.length}
            headerKey={task.key}
            filterData={getFilterData(task.key)}
            filterApplied={!!props.filterKeys?.[task.key]?.length}
            columnGroupClasses={checkAssignmentTaskColumnGroupClasses(
              taskIndex
            )}
          >
            {task.name}
          </CheckAssignmentTableHeaderCell>
        );
      })}
    </div>
  );
}
